import queryString from 'query-string'
import { TipoOrdenacao } from 'src/tipos'

import { gatewayApi } from '../instancias-api'

const urlBase = '/propostamatricula'

/* ==========================================================================
 * Tipos (espelham os DTOs do backend — Ipgs.Educacional.Application/.../Propostas)
 * ======================================================================== */

/** Espelha PropostaListaDto. */
export interface CondicaoEspecialListaDto {
  id: string
  numeroProtocolo: string
  /** Nome do aluno, se já assumida; senão o contato informado pelo consultor. */
  nomeContato: string
  cpf?: string
  curso?: string
  turma?: string
  /** Soma das opções mais baratas de cada destino — o "a partir de". */
  valorNegociado: number
  consultor?: string
  status: string
  statusPorExtenso: string
  dataAbertura: string
  validadeAte: string
  expirada: boolean
  /** Mesma regra do detalhe, calculada no backend. */
  podeEditar: boolean
}

/** Espelha ListarPropostasQueryResult. */
export interface ListarCondicoesEspeciaisResposta {
  itens: CondicaoEspecialListaDto[]
  quantidade: number
  temProximaPagina: boolean
}

/** Espelha OpcaoPagamentoDto. */
export interface OpcaoPagamentoDto {
  /** Id da opção no servidor; devolvido na edição para ela ser atualizada no lugar. */
  id: string
  destino: string
  formaPagamento: string
  formaPagamentoPorExtenso: string
  valor: number
  quantidadeParcelas: number
  valorParcela: number
  /** Calculado (valorTabela - valor), não persistido. */
  valorDesconto: number
}

/** Espelha PlanoNegociadoDto. */
export interface PlanoNegociadoDto {
  valorTabela: number
  taxaMatricula: number
  taxaMatriculaInclusa: boolean
  temEntrada: boolean
  dataInicioRecorrencia?: string
  opcoes: OpcaoPagamentoDto[]
}

/** Espelha HistoricoStatusDto. */
export interface HistoricoStatusCondicaoEspecialDto {
  id: string
  statusAnterior?: string
  statusAnteriorPorExtenso?: string
  statusNovo?: string
  statusNovoPorExtenso?: string
  usuarioId?: string
  usuarioNome?: string
  dataHora: string
  observacao?: string
  /** AlteracaoStatus, PropostaEditada ou ConviteEnviado. */
  acao: string
  acaoPorExtenso: string
  /** Nas edições e nos convites: o que mudou. Os campos de status ficam vazios. */
  campo?: string
  campoPorExtenso?: string
  valorAnterior?: string
  valorNovo?: string
}

/** Espelha PropostaDetalheDto. */
export interface CondicaoEspecialDetalheDto {
  id: string
  numeroProtocolo: string
  turmaId?: string
  turma?: string
  cursoId?: string
  curso?: string
  cpfAluno?: string
  emailConvite?: string
  nomeContato?: string
  alunoId?: string
  nomeAluno?: string
  matriculaGeradaId?: string
  /** Id do USUÁRIO do aluno: é o que a rota do painel financeiro usa. */
  usuarioAlunoId?: string
  consultor?: string
  status: string
  statusPorExtenso: string
  validadeAte: string
  expirada: boolean
  dataAbertura: string
  dataUltimaAlteracao: string
  motivoCancelamento?: string
  /** Reflete PodeEditar() do domínio — habilita a edição na tela. */
  podeEditar: boolean
  podeCancelar: boolean
  plano?: PlanoNegociadoDto
  historico: HistoricoStatusCondicaoEspecialDto[]
}

/** Espelha CriarPropostaCommandResult. */
export interface CriarCondicaoEspecialResposta {
  id: string
  numeroProtocolo: string
}

/** Espelha OpcaoPagamentoRequest. */
export interface OpcaoPagamentoRequest {
  /** Presente: atualiza a opção existente. Ausente: opção nova. */
  id?: string
  destino: string
  formaPagamento: string
  valor: number
  quantidadeParcelas: number
  valorParcela: number
}

/**
 * Corpo de criação/edição. `turmaId` é obrigatório — o curso da negociação vem
 * sempre da turma. `valorTabela` e `taxaMatricula` em branco fazem o backend
 * copiar do curso, congelando o preço da época.
 */
export interface DadosCondicaoEspecial {
  turmaId: string
  validadeAte: string
  cpfAluno?: string
  emailConvite?: string
  nomeContato?: string
  valorTabela?: number
  taxaMatricula?: number
  taxaMatriculaInclusa: boolean
  temEntrada: boolean
  dataInicioRecorrencia?: string
  opcoes: OpcaoPagamentoRequest[]
}

export interface FiltrosListagemCondicaoEspecial {
  nomeContato?: string
  cpf?: string
  numeroProtocolo?: string
  status?: string[]
  cursoId?: string
  turmaId?: string
  usuarioConsultorId?: string
  dataAberturaInicio?: string
  dataAberturaFim?: string
}

/* ==========================================================================
 * Chamadas
 *
 * Erros 400 (notificações do backend) já viram toast no interceptor do axios
 * (servicos/instancias-api.ts) — não é preciso tratá-los aqui.
 * ======================================================================== */

export const ListarCondicoesEspeciais = async (
  filtros: FiltrosListagemCondicaoEspecial,
  pagina: number,
  tamanhoPagina: number,
  chaveOrdenacao?: string,
  ordem?: TipoOrdenacao
): Promise<ListarCondicoesEspeciaisResposta | null> => {
  const query = queryString.stringify(
    {
      NomeContato: filtros.nomeContato,
      Cpf: filtros.cpf ? filtros.cpf.replace(/[.\-/]/g, '') : undefined,
      NumeroProtocolo: filtros.numeroProtocolo,
      Status: filtros.status,
      CursoId: filtros.cursoId,
      TurmaId: filtros.turmaId,
      UsuarioConsultorId: filtros.usuarioConsultorId,
      DataAberturaInicio: filtros.dataAberturaInicio,
      DataAberturaFim: filtros.dataAberturaFim,
      Pagina: pagina,
      TamanhoPagina: tamanhoPagina,
      ChaveOrdenacao: chaveOrdenacao,
      Ordem: ordem
    },
    { skipNull: true, skipEmptyString: true }
  )

  const { data } = await gatewayApi.get<ListarCondicoesEspeciaisResposta>(
    `${urlBase}?${query}`
  )

  return data
}

export const ObterCondicaoEspecialPorId = async (
  id: string
): Promise<CondicaoEspecialDetalheDto> => {
  const { data } = await gatewayApi.get<CondicaoEspecialDetalheDto>(
    `${urlBase}/${id}`
  )

  if (!data) {
    throw new Error('Condição especial não encontrada.')
  }

  return data
}

export const ObterHistoricoCondicaoEspecial = async (
  id: string
): Promise<HistoricoStatusCondicaoEspecialDto[]> => {
  const { data } = await gatewayApi.get<{
    itens: HistoricoStatusCondicaoEspecialDto[]
  }>(`${urlBase}/${id}/historico`)

  return data?.itens ?? []
}

export const CriarCondicaoEspecial = async (
  dados: DadosCondicaoEspecial
): Promise<CriarCondicaoEspecialResposta> => {
  const { data } = await gatewayApi.post<CriarCondicaoEspecialResposta>(
    urlBase,
    dados
  )

  return data
}

export const EditarCondicaoEspecial = async (
  id: string,
  dados: DadosCondicaoEspecial
): Promise<void> => {
  await gatewayApi.put(`${urlBase}/${id}`, dados)
}

/**
 * O aluno conseguirá ver o contrato do curso da turma? É por curso, não por turma.
 * Falso quando o contrato não está cadastrado OU o arquivo dele não está no S3: nos
 * dois casos o aluno não vê o PDF, só o resumo das cláusulas.
 */
export const VerificarContratoTurma = async (
  turmaId: string
): Promise<boolean> => {
  const { data } = await gatewayApi.get<{ possuiContrato: boolean }>(
    `${urlBase}/turma/${turmaId}/contrato`
  )

  return !!data?.possuiContrato
}

/** Envia (ou reenvia) o link da condição por e-mail, para o "E-mail de convite". */
export const EnviarConviteCondicaoEspecial = async (
  id: string
): Promise<{ id: string; email: string }> => {
  const { data } = await gatewayApi.post<{ id: string; email: string }>(
    `${urlBase}/${id}/enviar-convite`
  )

  return data
}

/**
 * Só aceita os status que o consultor controla (EmNegociacao, EnviadaAluno);
 * os demais são definidos pelo sistema e o backend recusa.
 */
export const AlterarStatusCondicaoEspecial = async (
  id: string,
  status: string,
  observacao: string
): Promise<void> => {
  await gatewayApi.patch(`${urlBase}/${id}/status`, { status, observacao })
}

export const CancelarCondicaoEspecial = async (
  id: string,
  motivo: string
): Promise<void> => {
  await gatewayApi.delete(`${urlBase}/${id}`, { data: { motivo } })
}
